import { supabase } from '../supabase';
import { ShipTrip } from '../utils/types';

export async function getShipTrips(): Promise<ShipTrip[]> {
  const { data, error } = await supabase
    .from('ship_trips')
    .select(`
      *,
      ship:ships!ship_trips_ship_id_fkey(*),
      market:markets!ship_trips_market_id_fkey(*),
      origin_market:markets!ship_trips_origin_market_id_fkey(*),
      settlements_count:settlements!ship_trip_id(count)
    `)
    .order('etd');

  if (error) {
    console.error('Error fetching ship trips:', error);
    return [];
  }

  return data;
}

export async function getShipTripByShipAndDate(
  shipId: number,
  date: string,
  isAir: boolean = false
): Promise<ShipTrip | null> {
  try {
    if (isAir) {
      // For air shipments, exact date match
      const { data, error } = await supabase
        .from('ship_trips')
        .select('*')
        .eq('ship_id', shipId)
        .eq('eta', date)
        .single();

      if (error) throw error;
      return data;
    } else {
      // For sea shipments, use ±2 day window
      const targetDate = new Date(date);
      const minDate = new Date(targetDate);
      const maxDate = new Date(targetDate);

      minDate.setDate(minDate.getDate() - 2);
      maxDate.setDate(maxDate.getDate() + 2);

      const { data, error } = await supabase
        .from('ship_trips')
        .select('*')
        .eq('ship_id', shipId)
        .gte('eta', minDate.toISOString().split('T')[0])
        .lte('eta', maxDate.toISOString().split('T')[0])
        .order('eta')
        .limit(1)
        .single();

      if (error) throw error;
      return data;
    }
  } catch (error) {
    console.error('Error finding ship trip:', error);
    return null;
  }
}

export async function getShipTripByShipNameAndDate(
  shipName: string,
  date: string
): Promise<{ id: number } | null> {
  try {
    const isAir = shipName.toUpperCase() === 'AEREO';
    const isGround = shipName.toUpperCase() === 'TERRESTRE';

    if (isAir || isGround) {
      // For air shipments, exact date match
      const { data, error } = await supabase
        .from('ship_trips')
        .select('id, ship:ship_id(name)')
        .eq('ship.name', shipName)
        .eq('eta', date)
        .single();

      if (error) throw error;
      return data;
    }  else {
      // For sea shipments, use ±2 day window
      const targetDate = new Date(date);
      const minDate = new Date(targetDate);
      const maxDate = new Date(targetDate);

      minDate.setDate(minDate.getDate() - 2);
      maxDate.setDate(maxDate.getDate() + 2);

      const { data, error } = await supabase
        .from('ship_trips')
        .select('id, ship:ship_id(name)')
        .eq('ship.name', shipName)
        .gte('eta', minDate.toISOString().split('T')[0])
        .lte('eta', maxDate.toISOString().split('T')[0])
        .order('eta')
        .limit(1)
        .single();

      if (error) throw error;
      return data;
    }
  } catch (error) {
    console.error('Error getting ship trip:', error);
    return null;
  }
}

export async function getShipTripById(id: number): Promise<ShipTrip | null> {
  const { data, error } = await supabase
    .from('ship_trips')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching ship trip:', error);
    return null;
  }

  return data;
}

export async function createShipTrip(
  shipTrip: Omit<ShipTrip, 'id' | 'created_at' | 'ship' | 'market'>
): Promise<ShipTrip | null> {
  const { data, error } = await supabase
    .from('ship_trips')
    .insert([shipTrip])
    .select()
    .single();

  if (error) {
    console.error('Error creating ship trip:', error);
    return null;
  }

  return data;
}

export async function updateShipTrip(
  id: number,
  shipTrip: Partial<ShipTrip>
): Promise<ShipTrip | null> {
  const { data, error } = await supabase
    .from('ship_trips')
    .update(shipTrip)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating ship trip:', error);
    return null;
  }

  return data;
}

export async function deleteShipTrip(id: number): Promise<boolean> {
  const { error } = await supabase.from('ship_trips').delete().eq('id', id);

  if (error) {
    console.error('Error deleting ship trip:', error);
    return false;
  }

  return true;
}

export async function findOrCreateAirShipTrip(
  etd: string,
  eta: string,
  pod: string,
  type: string
): Promise<ShipTrip | null> {
  try {
    // First, get the air ship (type "A")
    const { data: shipData, error: shipError } = await supabase
      .from('ships')
      .select('*')
      .eq('type', type)
      .eq('name', type === 'A' ? 'AEREO' : 'TERRESTRE')
      .single();

    if (shipError) throw shipError;
    
    // Try to find an existing ship trip with the same parameters
    const { data: existingTrip, error: findError } = await supabase
      .from('ship_trips')
      .select('*')
      .eq('etd', etd)
      .eq('eta', eta)
      .eq('market_id', pod)
      .eq('ship_id', shipData.id)
      .order('id')
      .limit(1)
      .maybeSingle();

    // If we found an existing trip, return it
    if (findError) throw findError;
    if (existingTrip) {
      return existingTrip;
    }

    // If no existing trip was found, create a new one
    const { data: newTrip, error: createError } = await supabase
      .from('ship_trips')
      .insert([{
        ship_id: shipData.id,
        etd: etd,
        eta: eta,
        market_id: pod
      }])
      .select()
      .single();

    if (createError) throw createError;
    return newTrip;
  } catch (error) {
    console.error('Error finding or creating air ship trip:', error);
    return null;
  }
}
